import { Check, Columns3 } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type ReactElement } from 'react';

import {
  focusMenuItem,
  menuButtonStart,
  onMenuKeyDown,
  type MenuStart,
} from '@/components/ui/menu-keys';
import { useDismiss } from '@/components/ui/use-dismiss';

import { OPTIONAL_COLUMNS, type ColumnId } from '../columns';
import type { CustomFieldColumn } from '../types';

// Selector "Columnas" de la Lista Maestra: la tabla parte de las columnas del mockup y
// aquí se suman los campos de la reunión (QA F1 #10) y los campos propios de la empresa.
// Menú de casillas (WAI-ARIA menuitemcheckbox): marcar no cierra el menú.

interface Props {
  columns: readonly ColumnId[];
  customColumns: CustomFieldColumn[];
  customKeys: readonly string[];
  onToggle: (id: ColumnId) => void;
  onToggleCustom: (key: string) => void;
  onReset: () => void;
}

export function ColumnsMenu({
  columns,
  customColumns,
  customKeys,
  onToggle,
  onToggleCustom,
  onReset,
}: Props): ReactElement {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<MenuStart>('first');

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, [wrapRef], buttonRef);

  useEffect(() => {
    if (open) {
      focusMenuItem(menuRef.current, startRef.current);
    }
  }, [open]);

  const extra =
    OPTIONAL_COLUMNS.filter((c) => columns.includes(c.id)).length +
    customColumns.filter((c) => customKeys.includes(c.key)).length;

  const item = (key: string, label: string, checked: boolean, onClick: () => void) => (
    <button
      key={key}
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      className="pop-item"
      onClick={onClick}
    >
      <span className="col-check" aria-hidden="true">
        {checked ? <Check size={14} /> : null}
      </span>
      {label}
    </button>
  );

  return (
    <div className="columns-menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="btn mut"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          startRef.current = 'first';
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          const start = menuButtonStart(e);
          if (start !== null) {
            startRef.current = start;
            if (open) {
              focusMenuItem(menuRef.current, start);
            } else {
              setOpen(true);
            }
          }
        }}
      >
        <Columns3 aria-hidden="true" strokeWidth={1.9} />
        {extra > 0 ? `Columnas · ${extra}` : 'Columnas'}
      </button>
      {open && (
        <div
          id={menuId}
          ref={menuRef}
          className="pop-menu"
          role="menu"
          tabIndex={-1}
          aria-label="Columnas de la Lista Maestra"
          onKeyDown={(e) => onMenuKeyDown(e, close)}
        >
          {OPTIONAL_COLUMNS.map((c) =>
            item(c.id, c.label, columns.includes(c.id), () => onToggle(c.id)),
          )}
          {customColumns.length > 0 && <div className="pop-sep" role="separator" />}
          {customColumns.map((c) =>
            item(`cf-${c.key}`, c.label, customKeys.includes(c.key), () => onToggleCustom(c.key)),
          )}
          <div className="pop-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="pop-item"
            onClick={() => {
              // Se cierra para que se note que se aplicó; el foco vuelve al botón.
              onReset();
              buttonRef.current?.focus();
              setOpen(false);
            }}
          >
            Restablecer columnas
          </button>
        </div>
      )}
    </div>
  );
}
